import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCalendar, FiCreditCard, FiFileText, FiUser, FiCheck } from 'react-icons/fi';
import { format, addDays } from 'date-fns';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import { Quote, Invoice } from '../../types';
import { convertQuoteToInvoice, validateQuoteForConversion } from '../../utils/quoteToInvoiceConverter';
import styles from './ConvertQuoteModal.module.css';

interface ConvertQuoteModalProps {
  quote: Quote;
  isOpen: boolean;
  onClose: () => void;
  onConvert: (invoice: Invoice) => void;
}

export function ConvertQuoteModal({ quote, isOpen, onClose, onConvert }: ConvertQuoteModalProps) {
  const [dueDate, setDueDate] = useState<Date>(addDays(new Date(), 30));
  const [notes, setNotes] = useState(quote.notes || '');
  const [terms, setTerms] = useState(quote.terms || '');
  const [isConverting, setIsConverting] = useState(false);

  const handleConvert = async () => {
    // Validate the quote
    const validation = validateQuoteForConversion(quote);
    if (!validation.isValid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }

    setIsConverting(true);
    try {
      // Convert quote to invoice
      const invoice = convertQuoteToInvoice({
        quote,
        dueDate,
        notes,
        terms
      });

      // Call the parent handler
      await onConvert(invoice);
      
      toast.success('Quote converted to invoice successfully!');
      onClose();
    } catch (error) {
      toast.error('Failed to convert quote to invoice');
    } finally {
      setIsConverting(false);
    }
  };

  const handleClose = () => {
    if (!isConverting) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
        >
          <motion.div
            className={styles.modal}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.header}>
              <h2 className={styles.title}>Convert Quote to Invoice</h2>
              <button
                className={styles.closeButton}
                onClick={handleClose}
                disabled={isConverting}
              >
                <FiX size={20} />
              </button>
            </div>

            <div className={styles.content}>
              {/* Quote Summary */}
              <div className={styles.quoteSummary}>
                <h3 className={styles.sectionTitle}>Quote Summary</h3>
                <div className={styles.summaryGrid}>
                  <div className={styles.summaryItem}>
                    <FiFileText size={16} />
                    <span className={styles.label}>Quote Number:</span>
                    <span className={styles.value}>{quote.quoteNumber}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <FiUser size={16} />
                    <span className={styles.label}>Client:</span>
                    <span className={styles.value}>{quote.client.name}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <FiCreditCard size={16} />
                    <span className={styles.label}>Total Amount:</span>
                    <span className={styles.value}>${quote.total.toFixed(2)}</span>
                  </div>
                  <div className={styles.summaryItem}>
                    <FiCalendar size={16} />
                    <span className={styles.label}>Issue Date:</span>
                    <span className={styles.value}>{format(quote.issueDate, 'MMM dd, yyyy')}</span>
                  </div>
                </div>
              </div>

              {/* Invoice Settings */}
              <div className={styles.invoiceSettings}>
                <h3 className={styles.sectionTitle}>Invoice Settings</h3>
                
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <FiCalendar size={16} />
                    Due Date *
                  </label>
                  <DatePicker
                    selected={dueDate}
                    onChange={(date: Date) => setDueDate(date)}
                    minDate={new Date()}
                    dateFormat="MMM dd, yyyy"
                    className={styles.datePicker}
                    placeholderText="Select due date"
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <FiFileText size={16} />
                    Notes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className={styles.textarea}
                    placeholder="Additional notes for the invoice..."
                    rows={3}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    <FiFileText size={16} />
                    Terms & Conditions
                  </label>
                  <textarea
                    value={terms}
                    onChange={(e) => setTerms(e.target.value)}
                    className={styles.textarea}
                    placeholder="Payment terms and conditions..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Preview */}
              <div className={styles.preview}>
                <h3 className={styles.sectionTitle}>Invoice Preview</h3>
                <div className={styles.previewCard}>
                  <div className={styles.previewHeader}>
                    <span className={styles.previewLabel}>Invoice Number:</span>
                    <span className={styles.previewValue}>INV-{new Date().getFullYear()}{String(new Date().getMonth() + 1).padStart(2, '0')}-XXX</span>
                  </div>
                  <div className={styles.previewHeader}>
                    <span className={styles.previewLabel}>Due Date:</span>
                    <span className={styles.previewValue}>{format(dueDate, 'MMM dd, yyyy')}</span>
                  </div>
                  <div className={styles.previewHeader}>
                    <span className={styles.previewLabel}>Total Amount:</span>
                    <span className={styles.previewValue}>${quote.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.footer}>
              <button
                className={styles.cancelButton}
                onClick={handleClose}
                disabled={isConverting}
              >
                Cancel
              </button>
              <button
                className={styles.convertButton}
                onClick={handleConvert}
                disabled={isConverting}
              >
                {isConverting ? (
                  <>
                    <div className={styles.spinner} />
                    Converting...
                  </>
                ) : (
                  <>
                    <FiCheck size={16} />
                    Convert to Invoice
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
